import { useEffect, useState, useRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CircleCheck, Loader2, ChevronDown } from "lucide-react";
import * as mutate from "@script/mutate";
import { DateUtils } from "@utils/dayUtill.ts";
import { cn } from "@utils/cn.ts";
import { useIsAdmin } from "@hooks/useAuth.ts";
import useRosterVersions from "@/hooks/roster-craete/useRosterVersions";
import { useScheduleStore } from "@/store/scheduleStore";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";

function ButtonRosterVersion({
  selectedGroupId,
  hasWanted,
  isLoadingWantedStatus,
}: {
  selectedGroupId: string | null;
  hasWanted?: boolean;
  isLoadingWantedStatus?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [versionName, setVersionName] = useState<string>("");

  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const prevScheduleIdRef = useRef<string | undefined>(undefined);

  const { curDate, curScheduleInfo, setCurScheduleInfo } = useScheduleStore();
  // ButtonGroup과 동일한 로직으로 groupId 계산 (쿼리 키 일치를 위해)
  const groupIdForQuery = isAdmin ? (selectedGroupId ?? null) : null;
  const { data: versionList, isLoading: isLoadingVersions } = useRosterVersions(
    {
      groupId: groupIdForQuery,
      enabled: hasWanted !== false,
    }
  );

  const isLoading = isLoadingWantedStatus || isLoadingVersions;
  const hasVersions = !!(versionList && versionList.length > 0);

  const currentVersionObj = useMemo(() => {
    if (!versionList) return null;
    return (
      versionList.find((v) => v.schedule_id === curScheduleInfo?.scheduleId) ||
      versionList.find((v) => v.version === curScheduleInfo?.version) ||
      [...versionList].sort((a, b) => b.version - a.version)[0]
    );
  }, [versionList, curScheduleInfo]);

  // 마감된 버전을 최상단에 정렬
  const sortedVersionList = useMemo(() => {
    if (!versionList) return [];
    return [...versionList].sort((a, b) => {
      // 마감된 버전(status === "issued")을 먼저
      if (a.status === "issued" && b.status !== "issued") return -1;
      if (a.status !== "issued" && b.status === "issued") return 1;
      // 같은 상태면 버전 번호 내림차순 (최신이 위로)
      return b.version - a.version;
    });
  }, [versionList]);

  // 상태 피드백 UseMemo임
  const displayValue = useMemo(() => {
    if (isLoading) return "버전 리스트를 불러오는 중입니다.";
    if (hasWanted === false) return "원티드 요청을 먼저해주세요.";
    if (!hasVersions) return "생성된 근무표가 없습니다.";
    if (isEditMode) return versionName;

    return (
      currentVersionObj?.name ||
      `${curDate?.month}월 근무표 VER  ${currentVersionObj?.version || ""}`
    );
  }, [
    isLoading,
    hasWanted,
    hasVersions,
    isEditMode,
    versionName,
    currentVersionObj,
    curDate,
  ]);

  useEffect(() => {
    if (currentVersionObj) {
      setVersionName(
        currentVersionObj.name ||
          `${curDate?.month}월 근무표 VER ${currentVersionObj.version}`
      );
    }
  }, [currentVersionObj, curDate, isEditMode]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      const timer = setTimeout(() => {
        if (scrollContainerRef.current) {
          const activeItem = scrollContainerRef.current.querySelector(
            ".current-active-item"
          );
          if (activeItem) {
            activeItem.scrollIntoView({ block: "center", behavior: "auto" });
          }
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isEditMode]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditMode]);

  // Schedule ID 변경 시 Popover 자동 닫기 (외부에서 변경된 경우만)
  useEffect(() => {
    const currentScheduleId = curScheduleInfo?.scheduleId;

    if (
      prevScheduleIdRef.current !== undefined &&
      prevScheduleIdRef.current !== currentScheduleId &&
      isOpen &&
      !isEditMode
    ) {
      setIsOpen(false);
    }
    // 현재 scheduleId를 이전 값으로 저장
    prevScheduleIdRef.current = currentScheduleId;
  }, [curScheduleInfo?.scheduleId, isOpen, isEditMode]);

  const handleSave = async () => {
    if (!curScheduleInfo?.scheduleId) return;
    const finalName = versionName.trim()
      ? versionName
      : `${curDate?.month}월 근무표 VER ${currentVersionObj?.version ?? 1}`;
    try {
      await mutate.saveScheduleVersionName(
        curScheduleInfo.scheduleId,
        finalName
      );
      queryClient.invalidateQueries({
        queryKey: ["VERSIONS", curDate?.year, curDate?.month],
      });
      toast.success("이름이 변경되었습니다.");
      setIsEditMode(false);
    } catch {
      toast.error("이름 변경에 실패했습니다.");
    }
  };

  return (
    <div className="relative h-full w-full max-w-[300px] flex items-center">
      {/* onOpenChange를 통해 외부 클릭이나 닫기 요청 시 상태 반영 */}
      <Popover
        open={isOpen && !isEditMode}
        onOpenChange={(open) => {
          if (!isEditMode) setIsOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <div className="relative  group h-13">
            <Input
              ref={inputRef}
              className={cn(
                "h-full pr-12 bg-white border-[#d6d6de] rounded-lg text-[14px]!  transition-all",
                // 마감 뱃지가 있으면 padding-left 조정
                currentVersionObj?.status === "issued" && !isEditMode
                  ? "pl-[75px] "
                  : "pl-9",
                !isEditMode &&
                  hasVersions &&
                  "cursor-pointer hover:border-gray-400"
              )}
              readOnly={!isEditMode}
              disabled={isLoading}
              value={displayValue}
              onChange={(e) => setVersionName(e.target.value)}
              onClick={() => !isEditMode && hasVersions && setIsOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setIsEditMode(false);
                if (e.key === "Enter") handleSave();
              }}
            />

            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                !isEditMode &&
                hasVersions && <ChevronDown className="w-4 h-4 text-black" />
              )}
            </div>

            {currentVersionObj?.status === "issued" && !isEditMode && (
              <span className="absolute left-9 top-1/2 -translate-y-1/2 bg-sig text-white py-[2px] px-1 text-xs rounded-[4px] pointer-events-none">
                마감
              </span>
            )}

            {!isAdmin && hasVersions && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {isEditMode ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <CircleCheck className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                      setIsEditMode(true);
                    }}
                    className="create_verNameModButton"
                  />
                )}
              </div>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[400px] p-0 shadow-xl border-[#d6d6de] overflow-hidden mt-1"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ul
            ref={scrollContainerRef}
            className="max-h-[300px] overflow-y-auto overscroll-contain py-1"
          >
            {sortedVersionList.map((ver) => {
              const isActive = ver.schedule_id === curScheduleInfo?.scheduleId;
              return (
                <li key={ver.schedule_id}>
                  <button
                    type="button"
                    className={cn(
                      "w-full flex items-center justify-between py-3.5 px-4 text-left hover:bg-gray-50 transition-colors",
                      isActive &&
                        "bg-blue-50 text-blue-600 font-semibold current-active-item"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      // 버전 선택 시 즉시 팝업 닫기 (UX 개선)
                      setIsOpen(false);
                      // 그 다음 전역 정보 업데이트 (비동기로 처리하여 Popover가 먼저 닫히도록)
                      setTimeout(() => {
                        setCurScheduleInfo({
                          scheduleId: ver.schedule_id,
                          version: ver.version,
                        });
                      }, 0);
                    }}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      {ver.status === "issued" && (
                        <span className="bg-sig mr-2 text-white p-1 px-1.5 text-xs rounded-sm">
                          마감
                        </span>
                      )}
                      <span className="truncate text-sm">
                        {ver.name ||
                          `${curDate?.month}월 근무표 VER ${ver.version}`}
                      </span>
                    </div>
                    <span className="shrink-0 text-[11px] text-gray-400 ml-3">
                      {DateUtils.dateFormatKR(ver.updated_at, "YYYY.MM.DD")}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default ButtonRosterVersion;
